import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeChecker } from '@attain-apps/type-guards'

import * as state from '../../../../../../../state'
import * as modals from '../../modals'

import * as blockCoding from './block-coding'
import * as recordFilters from './filters'

const BLOCK_CODING_TOOLTIP_MESSAGE = {
  blockCode: 'Block code selected records',
  clustered: 'Records assigned to a cluster cannot be block coded',
  loading: 'Selected records are still loading',
  noSelection: 'Select at least one record to block code',
  signedOff: 'Signed off records cannot be block coded',
} as const

const CLUSTER_ASSIGNMENT_TOOLTIP_MESSAGE = {
  assign: 'Assign selected records to a cluster',
  loading: 'Selected records are still loading',
  mixedOccupiers: 'Select records with the same occupier to assign a cluster',
  noSelection: 'Select at least one record to assign a cluster',
  signedOff: 'Signed off records cannot be assigned to a cluster',
  submitting: 'Cluster assignment is already in progress',
} as const

/**
 * Space records list page.
 */
@ngCore.Component({
  imports: [
    ds.table.Component,
    ds.layout.page.Component,
    ds.tooltip.Directive,
    blockCoding.Component,
    recordFilters.Component,
    modals.bulkComment.Component,
    modals.bulkFunctionalUsage.Component,
    modals.bulkResponsiblePersons.Component,
    modals.shared.createEmployeeForm.Component,
    modals.clusterAssignment.Component,
  ],
  selector: 'atn-space-records-list',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  // TODO: Possibly make an input for records collection that will allow reuse
  // between full and lite space
  /** @see {@link state.space.full.records.Collection} */
  public readonly recordsEc = ngCore.inject(state.space.full.records.Collection)

  /** @see {@link state.space.full.records.plugins.bulkComment.Plugin} */
  public readonly bulkCommentPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.bulkComment.Plugin)
    .get()

  /** @see {@link state.space.full.employee.Collection} */
  public readonly employeesEc = ngCore.inject(
    state.space.full.employee.Collection,
  )

  /** @see {@link state.space.full.records.plugins.bulkFunctionalUsage.Plugin} */
  public readonly bulkFunctionalUsagePlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.bulkFunctionalUsage.Plugin)
    .get()

  /** @see {@link state.space.full.records.plugins.bulkResponsiblePersons.Plugin} */
  public readonly bulkResponsiblePersonsPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.bulkResponsiblePersons.Plugin)
    .get()

  /** @see {@link state.space.full.records.plugins.clusterAssignment.Plugin} */
  public readonly clusterAssignmentPlugin = this.recordsEc.plugins
    .lookup(state.space.full.records.plugins.clusterAssignment.Plugin)
    .get()

  /** Whether records Current List params are applied. */
  public readonly hasAppliedParamsSg = ngCore.computed(
    (
      filters = this.recordsEc.plugins.currentListPlugin.appliedParams.selectors.filters.asSignal(),
    ) => Object.values(filters ?? {}).some(TypeChecker.isNotNullish.raw),
  )

  /** Whether the Block Coding dropdown is on screen */
  public readonly presentBlockCodingSg = ngCore.signal(false)

  /** @see {@link state.space.full.records.SPACE_RECORDS_SELECTION_TARGET} */
  public readonly selectionTarget =
    state.space.full.records.SPACE_RECORDS_SELECTION_TARGET

  /** Canonical table input for the full Space records list. */
  public readonly tableConfig = {
    collection: this.recordsEc,
    view: {
      pagination: true,
      selection: true,
      selectionTarget: this.selectionTarget,
      showColumnSettings: false,
      title: 'Records List',
    },
  } satisfies ds.table.ec.Config.Input<state.space.full.records.TypeParams>

  /** Selected record ids for records-list actions. */
  public readonly selectedRecordIdsSg = ngCore.computed(() =>
    this.recordsEc.plugins.selectionPlugin.feature.selectors
      .getAllSelectedIds({
        target: this.selectionTarget,
      })
      .asSignal()
      .filter(ents.extra.space.Record.Id.isId),
  )

  /** Selected records resolved from the records cache. */
  public readonly selectedRecordsSg = ngCore.computed(() =>
    this.recordsEc.plugins.selectionPlugin.feature.selectors
      .getAllSelected({
        target: this.selectionTarget,
      })
      .asSignal()
      .filter(TypeChecker.isNotNullish.raw),
  )

  /** Whether selected records cross occupier boundaries. */
  public readonly hasMixedSelectedOccupiersSg = ngCore.computed(
    (selectedRecords = this.selectedRecordsSg()) =>
      new Set(selectedRecords.map(record => record.occupierId)).size > 1,
  )

  /** Whether any selected record has already been signed off. */
  public readonly hasSignedOffSelectedSg = ngCore.computed(
    (selectedRecords = this.selectedRecordsSg()) =>
      selectedRecords.some(
        record =>
          record.surveyStatus === ents.extra.space.SurveyStatus.SignedOff,
      ),
  )

  /** Whether any selected record belongs to a cluster. */
  public readonly hasClusteredSelectedSg = ngCore.computed(
    (selectedRecords = this.selectedRecordsSg()) =>
      selectedRecords.some(record =>
        TypeChecker.isNotNullish(record.clusterId),
      ),
  )

  /** Whether the records-list block coding action is available. */
  public readonly canBlockCodeSg = ngCore.computed(
    (
      selectedRecordIds = this.selectedRecordIdsSg(),
      selectedRecords = this.selectedRecordsSg(),
      hasClusteredSelected = this.hasClusteredSelectedSg(),
      hasSignedOffSelected = this.hasSignedOffSelectedSg(),
    ) =>
      selectedRecordIds.length > 0 &&
      selectedRecordIds.length === selectedRecords.length &&
      !hasSignedOffSelected &&
      !hasClusteredSelected,
  )

  /** Tooltip for the records-list block coding action. */
  public readonly blockCodingTooltipSg = ngCore.computed(
    (
      selectedRecordIds = this.selectedRecordIdsSg(),
      selectedRecords = this.selectedRecordsSg(),
      hasClusteredSelected = this.hasClusteredSelectedSg(),
      hasSignedOffSelected = this.hasSignedOffSelectedSg(),
    ) => {
      if (!selectedRecordIds.length) {
        return BLOCK_CODING_TOOLTIP_MESSAGE.noSelection
      }

      if (selectedRecordIds.length !== selectedRecords.length) {
        return BLOCK_CODING_TOOLTIP_MESSAGE.loading
      }

      if (hasSignedOffSelected) {
        return BLOCK_CODING_TOOLTIP_MESSAGE.signedOff
      }

      if (hasClusteredSelected) {
        return BLOCK_CODING_TOOLTIP_MESSAGE.clustered
      }

      return BLOCK_CODING_TOOLTIP_MESSAGE.blockCode
    },
  )

  /** Whether the records-list cluster assignment action is available. */
  public readonly canAssignClusterSg = ngCore.computed(
    (
      selectedRecordIds = this.selectedRecordIdsSg(),
      selectedRecords = this.selectedRecordsSg(),
      hasMixedSelectedOccupiers = this.hasMixedSelectedOccupiersSg(),
      hasSignedOffSelected = this.hasSignedOffSelectedSg(),
    ) =>
      selectedRecordIds.length > 0 &&
      // Cluster options are occupier-scoped, so mixed occupiers cannot share
      // one valid assignment modal.
      selectedRecordIds.length === selectedRecords.length &&
      !hasMixedSelectedOccupiers &&
      // Signed off records are locked and cannot change cluster assignment.
      !hasSignedOffSelected &&
      !this.clusterAssignmentPlugin.selectors.isSubmitting.asSignal(),
  )

  /** Tooltip for the records-list cluster assignment action. */
  public readonly clusterAssignmentTooltipSg = ngCore.computed(
    (
      selectedRecordIds = this.selectedRecordIdsSg(),
      selectedRecords = this.selectedRecordsSg(),
      hasMixedSelectedOccupiers = this.hasMixedSelectedOccupiersSg(),
      hasSignedOffSelected = this.hasSignedOffSelectedSg(),
      isSubmitting = this.clusterAssignmentPlugin.selectors.isSubmitting.asSignal(),
    ) => {
      if (!selectedRecordIds.length) {
        return CLUSTER_ASSIGNMENT_TOOLTIP_MESSAGE.noSelection
      }

      if (selectedRecordIds.length !== selectedRecords.length) {
        return CLUSTER_ASSIGNMENT_TOOLTIP_MESSAGE.loading
      }

      if (hasSignedOffSelected) {
        return CLUSTER_ASSIGNMENT_TOOLTIP_MESSAGE.signedOff
      }

      if (hasMixedSelectedOccupiers) {
        return CLUSTER_ASSIGNMENT_TOOLTIP_MESSAGE.mixedOccupiers
      }

      if (isSubmitting) {
        return CLUSTER_ASSIGNMENT_TOOLTIP_MESSAGE.submitting
      }

      return CLUSTER_ASSIGNMENT_TOOLTIP_MESSAGE.assign
    },
  )

  /** Opens the bulk edit the user picked from Block Coding. */
  public blockCodingOptionSelectedHandler(
    option: state.space.full.records.models.BlockCodingOption,
  ): void {
    switch (option) {
      case state.space.full.records.models.BlockCodingOption.Comment:
        this.bulkCommentPlugin.actions.initiate
          .from('Space.RecordsList.BlockCoding.Comment.Clicked')
          .dispatch()
        break
      case state.space.full.records.models.BlockCodingOption.FunctionalUsage:
        this.bulkFunctionalUsagePlugin.actions.initiate
          .from('Space.RecordsList.BlockCoding.FunctionalUsage.Clicked')
          .dispatch()
        break
      case state.space.full.records.models.BlockCodingOption.ResponsiblePersons:
        this.bulkResponsiblePersonsPlugin.actions.initiate
          .from('Space.RecordsList.BlockCoding.ResponsiblePersons.Clicked')
          .dispatch()
        break
    }
  }

  /** Initiates cluster assignment for selected records. */
  public clusterAssignmentClickHandler(): void {
    if (!this.canAssignClusterSg()) {
      return
    }

    this.clusterAssignmentPlugin.actions.initiate
      .from('Space.RecordsList.ClusterAssignment.Icon.Clicked')
      .dispatch()
  }

  /** Starts editing the records Current List parameters. */
  public filtersClickHandler(): void {
    this.recordsEc.plugins.currentListPlugin.feature.actions.startEditingParams
      .from('Space.RecordsList.Filters.Icon.Clicked')
      .dispatch()
  }
}
